import React, { useState, useEffect, useCallback } from 'react';
import { getCajaMovementsByLocalAndDateRange } from '../../../../../../../../../crud/payments_api/caja_movements';
import { getUsersByLocalId } from '../../../../../../../../../crud/users_api/users';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../../../../../../../components/common/LoadingSpinner';
import { formatCurrency } from '../../../../../../../../../utils/functions';
import CheckboxMultiSelect from '../../../../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import MovementDetailsModal from '../../../../../../../Caja/CajasManagement/components/HistorialMovimientos/components/Table/components/MovementDetailsModal';
import './Movimientos.scss';

interface CajaMovement {
  caja_movement_id: number;
  workstation_id: number;
  workstation_name: string;
  user_id: number;
  user_name: string;
  before_amount: number;
  after_amount: number;
  amount: number;
  description: string | null;
  is_positive: boolean;
  caja_movement_type_id: number;
  caja_movement_type_name: string;
  caja_movement_metadata: any;
  timestamp: string;
  local_timestamp: string;
  session_id?: number;
  shift_id?: number | null;
  shift_name?: string | null;
  receipt_id?: number | null;
}

interface MovimientosProps {
  isActive: boolean;
}

const Movimientos: React.FC<MovimientosProps> = ({ isActive }) => {
  const [movements, setMovements] = useState<CajaMovement[]>([]);
  const [filteredMovements, setFilteredMovements] = useState<CajaMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState<CajaMovement | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Filter states
  const [selectedTypes, setSelectedTypes] = useState<{value: any, label: string}[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{value: any, label: string}[]>([]);

  // Filter options
  const [typeOptions, setTypeOptions] = useState<{value: any, label: string}[]>([]);
  const [userOptions, setUserOptions] = useState<{value: any, label: string}[]>([]);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;

    // Get today's movements
    const today = new Date();
    const startDate = today.toISOString().split('T')[0];
    const endDate = today.toISOString().split('T')[0];

    try {
      const response = await getCajaMovementsByLocalAndDateRange(localId, startDate, endDate);
      const validMovements = response.data.filter((m: CajaMovement) => {
        return m.local_timestamp && m.local_timestamp !== '' && !isNaN(new Date(m.local_timestamp).getTime());
      });
      
      // Sort by timestamp descending (most recent first)
      const sortedMovements = validMovements.sort((a: CajaMovement, b: CajaMovement) => {
        const dateA = new Date(a.local_timestamp).getTime();
        const dateB = new Date(b.local_timestamp).getTime();
        return dateB - dateA; // Descending order (most recent first)
      });
      
      setMovements(sortedMovements);
      setFilteredMovements(sortedMovements);
      setDataLoaded(true);

      // Extract unique filter options
      extractFilterOptions(sortedMovements);
    } catch (error) {
      console.error('Error loading movements:', error);
      toast.error('Error cargando movimientos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch movements when component becomes active
  useEffect(() => {
    if (isActive && !dataLoaded) {
      fetchMovements();
    }
  }, [isActive, dataLoaded, fetchMovements]);

  const extractFilterOptions = (data: CajaMovement[]) => {
    // Extract movement types
    const typesMap = new Map<string, string>();
    data.forEach(m => {
      if (m.caja_movement_type_name) {
        typesMap.set(m.caja_movement_type_name, m.caja_movement_type_name);
      }
    });
    setTypeOptions(
      Array.from(typesMap.entries()).map(([name]) => ({ value: name, label: name }))
    );

    // Extract users
    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;
    
    getUsersByLocalId(localId).then((userRes: any) => {
      const users = userRes.data
        .filter((user: any) => user && (user.id || user.user_id) && (user.name || user.username || user.user_name))
        .map((user: any) => ({
          label: user.name && user.lastname 
            ? `${user.name} ${user.lastname}` 
            : user.username || user.user_name || `Usuario ${user.id || user.user_id}`,
          value: user.id || user.user_id
        }));
      setUserOptions(users);
    }).catch((err: any) => {
      console.error('Error getting users:', err);
      setUserOptions([]);
    });
  };

  // Apply filters
  useEffect(() => {
    let filtered = movements;

    if (selectedTypes.length > 0) {
      const typeValues = selectedTypes.map(t => t.value);
      filtered = filtered.filter(m => typeValues.includes(m.caja_movement_type_name));
    }

    if (selectedUsers.length > 0) {
      const userIds = selectedUsers.map(u => u.value);
      filtered = filtered.filter(m => userIds.includes(m.user_id));
    }

    setFilteredMovements(filtered);
  }, [movements, selectedTypes, selectedUsers]);

  const handleOpenModal = useCallback((movement: CajaMovement) => {
    setShowModal(false);
    setSelectedMovement(null);
    
    setTimeout(() => {
      setSelectedMovement(movement);
      setShowModal(true);
    }, 10);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedMovement(null);
  }, []);

  const formatDateOrTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  if (!isActive) {
    return null;
  }

  if (loading && !dataLoaded) {
    return (
      <div className="movimientos-panel">
        <LoadingSpinner text="Cargando movimientos..." transparent />
      </div>
    );
  }

  return (
    <div className="movimientos-panel">
      {/* Filters Section */}
      <div className="movimientos-filters">
        <div className="filter-item">
          <CheckboxMultiSelect
            options={typeOptions}
            value={selectedTypes}
            onChange={setSelectedTypes}
            placeholder="Tipo"
          />
        </div>
        <div className="filter-item">
          <CheckboxMultiSelect
            options={userOptions}
            value={selectedUsers}
            onChange={setSelectedUsers}
            placeholder="Usuario"
          />
        </div>
      </div>

      {/* Movements List */}
      <div className="movimientos-list">
        {filteredMovements.length === 0 ? (
          <div className="movimientos-empty">
            <p>No hay movimientos para mostrar</p>
          </div>
        ) : (
          filteredMovements.map(movement => (
            <div 
              key={movement.caja_movement_id} 
              className="movimiento-card"
              onClick={() => handleOpenModal(movement)}
            >
              {/* Row 1: Type and Time */}
              <div className="movimiento-card-row-1">
                <div className="movimiento-card-type">
                  {movement.caja_movement_type_name}
                </div>
                <div className="movimiento-card-time">
                  {formatDateOrTime(movement.local_timestamp)}
                </div>
              </div>

              {/* Row 2: User and Amount */}
              <div className="movimiento-card-row-2">
                <div className="movimiento-card-user">
                  {movement.user_name}
                </div>
                <div className="movimiento-card-amounts">
                  <span className="amount-before">{formatCurrency(movement.before_amount)}</span>
                  <span className="arrow">→</span>
                  <span className={`amount-after ${movement.is_positive ? 'positive' : 'negative'}`}>
                    {formatCurrency(movement.after_amount)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Movement Details Modal */}
      {selectedMovement && showModal && (
        <MovementDetailsModal
          show={showModal}
          onClose={handleCloseModal}
          movement={selectedMovement}
        />
      )}
    </div>
  );
};

export default Movimientos;
